import { useEffect, useMemo, useState } from 'react'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  LineChart,
  Line,
} from 'recharts'
import { MarketPositionScale } from './MarketPositionScale'
import { fetchWithTimeout } from '../utils/fetchWithTimeout'
import { formatRecentDropLine, hasRecentPriceDropChf, recentDropChfFromHistoriqueRows } from '../utils/recentPriceDrop'
import { applyClusterJitter } from '../utils/scatterJitter'
import { filterScatterMarketPoints } from '../utils/scatterDedup'
import { BACKEND_URL as BACKEND } from '../config/backendUrl'

const MS_PER_DAY = 86400000

const sectionTitle = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  fontVariant: 'small-caps',
  marginBottom: 8,
}

const insightCard = {
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '14px 16px',
  background: 'rgba(255,255,255,0.02)',
  marginBottom: 14,
}

/** Si `make` est absent en base, premier mot du titre (ex. « honda cb… »). */
function inferMakeFromTitre(titre) {
  const t = String(titre || '').trim().toLowerCase()
  if (!t) return ''
  if (t.startsWith('royal enfield')) return 'royal enfield'
  return t.split(/\s+/)[0] || ''
}

function scaleFromCotationEngine(c) {
  if (!c) return null
  const lo = Number(c.fourchette_basse)
  const hi = Number(c.fourchette_haute)
  let median = Number(c.valeur_estimee)
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null
  if (!Number.isFinite(median)) median = (lo + hi) / 2
  median = Math.min(hi, Math.max(lo, median))
  return { lo, hi, median }
}

function pickCoterParams(record, variant) {
  const model = String(record?.model || '').trim()
  let make = String(record?.make || '').trim().toLowerCase()
  if (!make) make = inferMakeFromTitre(record?.titre)
  const km = Number(record?.km)
  const annee = parseInt(String(record?.first_registration_year ?? record?.annee ?? ''), 10)
  let prixActuel = null
  if (variant === 'baisse') prixActuel = record?.nouveau_prix != null ? Number(record.nouveau_prix) : null
  else if (record?.prix_chf != null && Number.isFinite(Number(record.prix_chf))) prixActuel = Number(record.prix_chf)
  else if (record?.prix != null && Number.isFinite(Number(record.prix))) prixActuel = Number(record.prix)
  return { make, model, annee, km, prixActuel, titre: String(record?.titre || '') }
}

function buildScatterDomains(others, kmH, prixH) {
  const kms = [...others.map((p) => p.km), ...(Number.isFinite(kmH) ? [kmH] : [])]
  const prixs = [...others.map((p) => p.prix), ...(Number.isFinite(prixH) ? [prixH] : [])]
  if (!kms.length) return null
  const kmMin = Math.min(...kms)
  const kmMax = Math.max(...kms)
  const pMin = Math.min(...prixs)
  const pMax = Math.max(...prixs)
  const padKm = Math.max(400, (kmMax - kmMin) * 0.1)
  const padP = Math.max(200, (pMax - pMin) * 0.1)
  return {
    kmMin: Math.max(0, kmMin - padKm),
    kmMax: kmMax + padKm,
    prixMin: Math.max(0, pMin - padP),
    prixMax: pMax + padP,
  }
}

function scatterTitleFromUrl(url) {
  if (!url) return ''
  try {
    const parsed = new URL(url)
    const slug = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() || '')
    const cleaned = slug.replace(/\.(html?|php)$/i, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim()
    return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : ''
  } catch {
    return ''
  }
}

function normalizeYear(value) {
  const y = parseInt(String(value ?? ''), 10)
  return Number.isFinite(y) ? y : null
}

function pointTone(kind, isSameYear) {
  if (kind === 'highlight') {
    // Bleu marine (identique au scatter de la page Cotation)
    if (isSameYear) return { fill: '#001E50', stroke: '#7ea6ff' }
    return { fill: 'rgba(0,30,80,0.65)', stroke: 'rgba(126,166,255,0.7)' }
  }
  // FIX 2026-05 : les ventes confirmées sont en rouge ouvert (comme la page
  // Cotation) pour bien les distinguer des annonces actives.
  if (kind === 'vendue') return { fill: 'rgba(248,113,113,0.55)', stroke: '#f87171' }
  if (isSameYear) return { fill: 'rgba(252,252,255,0.9)', stroke: 'rgba(255,255,255,0.95)' }
  return { fill: 'rgba(148,163,184,0.18)', stroke: 'rgba(148,163,184,0.3)' }
}

export function MarchePanelInsights({
  token,
  record,
  variant,
  backend = BACKEND,
  showPositionMarcheIntro = true,
  compactTop = false,
  omitMarketPositionScale = false,
  omitHistoriqueChart = false,
  /** Dans la fiche Mon stock : évite le doublon avec le bloc liquidité / tableau similaires déjà affichés dans le parent. */
  omitLiquidityBlock = false,
  omitSimilairesExcerpt = false,
}) {
  const [cotPayload, setCotPayload] = useState(null)
  const [histSeries, setHistSeries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const params = useMemo(() => pickCoterParams(record, variant), [record, variant])
  const idSource = record?.id_source ? String(record.id_source) : ''

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setLoading(false)
      setError(null)
      setCotPayload(null)
      return undefined
    }
    if (!params.make || !params.model || !Number.isFinite(params.annee) || !Number.isFinite(params.km)) {
      setLoading(false)
      setError(null)
      setCotPayload(null)
      return undefined
    }
    setLoading(true)
    setError(null)
    setCotPayload(null)
    fetchWithTimeout(
      `${backend}/api/coter`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          make: params.make,
          model: params.model,
          annee: params.annee,
          km: params.km,
          titre_listing: params.titre,
          garantie: Boolean(record?.garantie),
          had_accident: Boolean(record?.had_accident),
        }),
      },
      120000
    )
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (cancelled) return
        if (!ok) throw new Error(j?.error || 'Analyse indisponible')
        setCotPayload(j)
      })
      .catch((e) => {
        if (!cancelled) {
          const msg = String(e?.message || '')
          const isNetwork =
            msg === 'Failed to fetch' ||
            msg.includes('NetworkError') ||
            e?.name === 'TypeError'
          setError(
            isNetwork
              ? `Impossible de joindre l’API (${backend || 'proxy /api'}). Vérifiez que le backend tourne sur le port 3001 (npm run backend).`
              : msg || 'Erreur'
          )
          setCotPayload(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, backend, params.make, params.model, params.annee, params.km, params.titre, record?.garantie, record?.had_accident])

  useEffect(() => {
    let cancelled = false
    if (omitHistoriqueChart || !idSource) {
      setHistSeries([])
      return undefined
    }
    fetchWithTimeout(`${backend}/api/annonces/${encodeURIComponent(idSource)}/historique`)
      .then((r) => r.json())
      .then((rows) => {
        if (cancelled) return
        const arr = Array.isArray(rows) ? rows : []
        const sorted = [...arr].sort((a, b) => new Date(a.cree_at).getTime() - new Date(b.cree_at).getTime())
        setHistSeries(
          sorted.map((h, idx) => {
            const delta = Number(h.nouveau_prix) - Number(h.ancien_prix)
            const at = h.cree_at != null ? new Date(h.cree_at).getTime() : NaN
            const ageDays = Number.isFinite(at) ? (Date.now() - at) / MS_PER_DAY : NaN
            const isRecentDrop =
              Number.isFinite(delta) &&
              delta < 0 &&
              Number.isFinite(ageDays) &&
              ageDays >= 0 &&
              ageDays <= 14
            return {
              idx: idx + 1,
              t: h.cree_at,
              prix: Number(h.nouveau_prix),
              baisse_recente_ce_point: isRecentDrop,
            }
          })
        )
      })
      .catch(() => {
        if (!cancelled) setHistSeries([])
      })
    return () => {
      cancelled = true
    }
  }, [backend, idSource, omitHistoriqueChart])

  const cotation = cotPayload?.cotation
  const velocite = cotPayload?.velocite
  const graphiquesKey = cotPayload?.cotation?.annonces_graphiques
  const similaires = Array.isArray(cotation?.annonces_similaires) ? cotation.annonces_similaires : []

  const recordUrl = record?.url ? String(record.url) : ''
  const modelYearRef = normalizeYear(params.annee)
  const ventesKey = cotation?.stats_ventes?.dernieres_ventes
  const highlightVehicle = useMemo(() => {
    if (!Number.isFinite(params.km) || !Number.isFinite(params.prixActuel)) return null
    return {
      km: params.km,
      prix: params.prixActuel,
      annee: modelYearRef,
      url: recordUrl || null,
    }
  }, [params.km, params.prixActuel, modelYearRef, recordUrl])

  const others = useMemo(() => {
    const graphPts = Array.isArray(graphiquesKey) ? graphiquesKey : []
    const actifsRaw = graphPts
      .filter((p) => Number.isFinite(Number(p.km)) && Number.isFinite(Number(p.prix)))
      .map((p) => {
        const pointYear = normalizeYear(p.annee ?? p.first_registration_year)
        return {
          km: Number(p.km),
          prix: Number(p.prix),
          annee: pointYear,
          titre: p.titre || p.title || scatterTitleFromUrl(p.url) || null,
          ville: p.ville || null,
          canton: p.canton || null,
          garantie: p.garantie === true,
          url: p.url || null,
          image_url: p.image_url || p.image_principale_url || null,
          baisse_recente_chf:
            Number.isFinite(Number(p.baisse_recente_chf)) && Number(p.baisse_recente_chf) > 0
              ? Number(p.baisse_recente_chf)
              : null,
          isSameModelYear: modelYearRef != null && pointYear != null ? pointYear === modelYearRef : true,
          type: 'annonce_marche',
        }
      })
    const actifs = highlightVehicle
      ? filterScatterMarketPoints(actifsRaw, highlightVehicle, recordUrl)
      : actifsRaw
    // Ventes : même fenêtre ±2 ans que le moteur de cotation (pas année exacte seule).
    const ventes = (Array.isArray(ventesKey) ? ventesKey : [])
      .filter((v) => {
        if (recordUrl && v?.url === recordUrl) return false
        if (!Number.isFinite(Number(v?.km)) || !Number.isFinite(Number(v?.prix))) return false
        if (modelYearRef == null) return true
        const y = normalizeYear(v?.annee ?? v?.first_registration_year)
        return y != null && Math.abs(y - modelYearRef) <= 2
      })
      .map((v) => {
        const pointYear = normalizeYear(v.annee ?? v.first_registration_year)
        return {
          km: Number(v.km),
          prix: Number(v.prix),
          annee: pointYear,
          titre: v.titre || v.title || scatterTitleFromUrl(v.url) || null,
          ville: v.ville || null,
          canton: v.canton || null,
          garantie: v.garantie === true,
          url: v.url || null,
          image_url: v.image_url || v.image_principale_url || null,
          baisse_recente_chf:
            Number.isFinite(Number(v.baisse_recente_chf)) && Number(v.baisse_recente_chf) > 0
              ? Number(v.baisse_recente_chf)
              : null,
          isSameModelYear: true,
          date_vente: v.date_vente || null,
          jours_en_ligne: Number.isFinite(Number(v.jours_en_ligne)) ? Number(v.jours_en_ligne) : null,
          type: 'vendue',
        }
      })
    return [...actifs, ...ventes]
  }, [graphiquesKey, ventesKey, modelYearRef, recordUrl, highlightVehicle])

  const highlightPt = useMemo(() => {
    if (!Number.isFinite(params.km) || !Number.isFinite(params.prixActuel)) return []
    return [
      (() => {
        const pointYear = normalizeYear(params.annee)
        const fromRecord =
          Number.isFinite(Number(record?.baisse_recente)) && Number(record.baisse_recente) > 0
            ? Number(record.baisse_recente)
            : Number.isFinite(Number(record?.baisse_recente_chf)) && Number(record.baisse_recente_chf) > 0
              ? Number(record.baisse_recente_chf)
              : null
        const fromCotation = recentDropChfFromHistoriqueRows(cotPayload?.cotation?.historique_prix?.historique)
        const merged = hasRecentPriceDropChf(fromRecord) ? fromRecord : fromCotation
        return {
        km: params.km,
        prix: params.prixActuel,
        annee: pointYear,
        titre: params.titre || record?.titre || 'Cette annonce',
        ville: record?.ville || null,
        canton: record?.canton || null,
        garantie: record?.garantie === true,
        url: recordUrl || null,
        image_url: record?.image_url || record?.image_principale_url || null,
        isSameModelYear: modelYearRef != null && pointYear != null ? pointYear === modelYearRef : true,
        type: 'votre_annonce',
        baisse_recente_chf: hasRecentPriceDropChf(merged) ? merged : null,
        }
      })(),
    ]
  }, [
    params.km,
    params.prixActuel,
    params.annee,
    params.titre,
    modelYearRef,
    record?.titre,
    record?.ville,
    record?.canton,
    record?.garantie,
    record?.image_url,
    record?.image_principale_url,
    record?.baisse_recente,
    record?.baisse_recente_chf,
    recordUrl,
    cotPayload?.cotation?.historique_prix?.historique,
  ])

  const domains = useMemo(
    () => buildScatterDomains(others, params.km, params.prixActuel),
    [others, params.km, params.prixActuel]
  )

  const valeurEstimee = Number(cotation?.valeur_estimee)
  const ScatterTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const d = payload[0]?.payload
    if (!d) return null
    const prix = Number(d?.prixReal != null ? d.prixReal : d?.prix)
    const km = Number(d?.kmReal != null ? d.kmReal : d?.km)
    const dropLine = formatRecentDropLine(d.baisse_recente_chf)
    return (
      <div
        style={{
          background: 'var(--surface-alt)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '8px 10px',
          fontSize: 12,
          maxWidth: 320,
        }}
      >
        {d.image_url ? (
          <img
            src={d.image_url}
            alt=""
            style={{ width: '100%', maxWidth: 300, height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 6 }}
          />
        ) : null}
        <div style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 4 }}>
          {d.titre ||
            (d.type === 'votre_annonce'
              ? 'Cette annonce'
              : d.type === 'vendue'
                ? 'Vente confirmée'
                : 'Annonce marché')}
        </div>
        {d.type === 'vendue' ? (
          <div style={{ color: '#f87171', fontSize: 11, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Vente confirmée
          </div>
        ) : null}
        <div style={{ color: '#f8fafc', fontWeight: 600 }}>
          {Number.isFinite(prix) ? `${prix.toLocaleString('fr-CH')} CHF` : 'Prix indisponible'}
        </div>
        {dropLine ? (
          <div style={{ color: '#fecaca', fontSize: 11, fontWeight: 600, marginTop: 4 }}>{dropLine}</div>
        ) : null}
        <div style={{ color: 'var(--text-tertiary)' }}>
          {Number.isFinite(km) ? `${km.toLocaleString('fr-CH')} km` : 'km n.d.'}
          {d.annee ? ` · ${d.annee}` : ''}
        </div>
        {d.type === 'vendue' && d.date_vente ? (
          <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
            Vendue le {new Date(d.date_vente).toLocaleDateString('fr-CH')}
            {Number.isFinite(d.jours_en_ligne) && d.jours_en_ligne > 0
              ? ` · ${d.jours_en_ligne} j en ligne`
              : ''}
          </div>
        ) : null}
        {(d.ville || d.canton) && (
          <div style={{ color: 'var(--text-tertiary)' }}>
            {[d.ville, d.canton].filter(Boolean).join(' · ')}
          </div>
        )}
        {d.garantie ? <div style={{ color: '#4ade80', fontSize: 11, marginTop: 2 }}>Garantie</div> : null}
        {d.url ? (
          <div style={{ color: 'rgba(226,232,240,0.55)', fontSize: 10, marginTop: 3 }}>
            Double-cliquez pour ouvrir l&apos;annonce
          </div>
        ) : null}
      </div>
    )
  }

  const ScatterPointShape = ({ cx, cy, strokeWidth, payload, r = 5 }) => {
    const kind =
      payload?.type === 'votre_annonce'
        ? 'highlight'
        : payload?.type === 'vendue'
          ? 'vendue'
          : 'annonce'
    const tone = pointTone(kind, payload?.isSameModelYear !== false)
    const recentDrop = hasRecentPriceDropChf(payload?.baisse_recente_chf)
    return (
    <g
      onDoubleClick={(e) => {
        e.stopPropagation()
        if (payload?.url) window.open(payload.url, '_blank', 'noopener,noreferrer')
      }}
      style={{ cursor: payload?.url ? 'pointer' : 'default' }}
    >
      {recentDrop ? (
        <circle
          cx={cx}
          cy={cy}
          r={r + 3}
          fill="none"
          stroke="rgba(248,113,113,0.72)"
          strokeWidth={2}
          pointerEvents="none"
        />
      ) : null}
      <circle cx={cx} cy={cy} r={r} fill={tone.fill} stroke={tone.stroke} strokeWidth={strokeWidth} />
    </g>
    )
  }

  const showScatter = domains && (others.length > 0 || highlightPt.length > 0)
  const scatterData = useMemo(() => {
    // Jitter combiné sur l'union des points pour qu'aucune série ne masque
    // l'autre quand plusieurs véhicules ont le même couple (km, prix).
    const all = [...others, ...highlightPt.map((p) => ({ ...p, _isYou: true }))]
    const jittered = domains ? applyClusterJitter(all, domains) : all
    return {
      memeAnnee: jittered.filter((p) => !p._isYou && p.type !== 'vendue' && p.isSameModelYear !== false),
      autreAnnee: jittered.filter((p) => !p._isYou && p.type !== 'vendue' && p.isSameModelYear === false),
      vendues: jittered.filter((p) => !p._isYou && p.type === 'vendue'),
      votreAnnonce: jittered.filter((p) => p._isYou),
    }
  }, [others, highlightPt, domains])
  const cotationScale = useMemo(() => scaleFromCotationEngine(cotation), [cotation])

  const wrapTop = compactTop
    ? { marginTop: 0, paddingTop: 0, borderTop: 'none' }
    : { marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--border)' }

  return (
    <div style={wrapTop}>
      {!params.make && !loading ? (
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>
          Analyse marché (nuage prix/km, fourchette) indisponible : marque et titre insuffisants. Rechargez les données marché ou
          complétez l’annonce en base.
        </p>
      ) : null}
      {showPositionMarcheIntro && (record?.positionnement_marche != null || record?.ecart_vs_mediane != null) ? (
        <div style={{ ...insightCard, marginBottom: 16 }}>
          <div style={sectionTitle}>Position vs annonces actives (même modèle)</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {record?.positionnement_marche ? (
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{String(record.positionnement_marche)}</span>
            ) : null}
            {Number.isFinite(Number(record?.ecart_vs_mediane)) ? (
              <span style={{ marginLeft: 8 }}>
                Écart médiane :{' '}
                <span style={{ fontWeight: 600, color: Number(record.ecart_vs_mediane) <= 0 ? '#86efac' : '#fca5a5' }}>
                  {Number(record.ecart_vs_mediane) > 0 ? '+' : ''}
                  {Number(record.ecart_vs_mediane)} %
                </span>
              </span>
            ) : null}
            {Number.isFinite(Number(record?.prix_median)) ? (
              <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'var(--text-tertiary)' }}>
                Prix médian observé (90 j) : {Number(record.prix_median).toLocaleString('fr-CH')} CHF
                {record?.nb_annonces_mediane != null ? ` · ${record.nb_annonces_mediane} annonces` : null}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? <p style={{ fontSize: 12, color: '#fca5a5', marginBottom: 12 }}>{error}</p> : null}

      {!loading && cotation && !error ? (
        <>
          {!omitMarketPositionScale ? (
            variant !== 'opportunite' ? (
              <div style={insightCard}>
                {cotationScale && Number.isFinite(params.prixActuel) ? (
                  <MarketPositionScale
                    prix={params.prixActuel}
                    scale={cotationScale}
                    dotLabel="Cette annonce"
                    title="Position dans la fourchette"
                    axisLabels={{ left: 'Plancher', center: 'Référence', right: 'Plafond' }}
                  />
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
                    Fourchette de prix indisponible pour placer cette annonce.
                  </div>
                )}
              </div>
            ) : (
              <div style={insightCard}>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.45, marginBottom: 12 }}>
                  Nuage, liquidité et comparables — le positionnement prix est dans l’en-tête de cette fiche.
                </div>
                {cotationScale && Number.isFinite(params.prixActuel) ? (
                  <MarketPositionScale
                    prix={params.prixActuel}
                    scale={cotationScale}
                    dotLabel="Cette annonce"
                    title="Position dans la fourchette"
                    axisLabels={{ left: 'Plancher', center: 'Référence', right: 'Plafond' }}
                  />
                ) : null}
              </div>
            )
          ) : null}

          {!omitLiquidityBlock && velocite && !velocite.erreur ? (
            <div style={insightCard}>
              <div style={sectionTitle}>Liquidité du modèle</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Délai de vente estimé :{' '}
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>
                  {velocite.jours_estimes != null ? `~${velocite.jours_estimes} jours` : '—'}
                </span>
                {velocite.nb_actives != null ? (
                  <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {velocite.nb_actives} annonce{velocite.nb_actives > 1 ? 's' : ''} active{velocite.nb_actives > 1 ? 's' : ''}
                    {velocite.nb_vendues ? ` · ${velocite.nb_vendues} ventes analysées` : null}
                  </span>
                ) : null}
                {velocite.interpretation ? (
                  <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {velocite.interpretation}
                    {velocite.commentaire_prix ? ` · ${velocite.commentaire_prix}` : ''}
                  </span>
                ) : null}
              </div>
            </div>
          ) : !omitLiquidityBlock && velocite?.erreur ? (
            <div style={{ ...insightCard, fontSize: 12, color: 'var(--text-tertiary)' }}>Liquidité : données insuffisantes.</div>
          ) : null}

          {Array.isArray(ventesKey) && ventesKey.length > 0 ? (
            <div style={insightCard}>
              <div style={sectionTitle}>Ventes récentes (base cotation)</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10, lineHeight: 1.45 }}>
                Motos vendues récemment pour ce modèle — points rouges sur le graphique ci-dessous.
              </div>
              <div
                style={{
                  maxHeight: 220,
                  overflowY: 'auto',
                  border: '1px solid rgba(248,113,113,0.2)',
                  borderRadius: 6,
                  background: 'rgba(127,29,29,0.06)',
                }}
              >
                {ventesKey.slice(0, 12).map((v, i, arr) => {
                  const prix = Number(v.prix)
                  const kmV = Number(v.km)
                  const y = normalizeYear(v.annee)
                  return (
                    <div
                      key={`${v.url || i}-${i}`}
                      style={{
                        padding: '8px 10px',
                        borderBottom: i < arr.length - 1 ? '1px solid rgba(248,113,113,0.12)' : 'none',
                        fontSize: 12,
                      }}
                    >
                      <div style={{ color: '#fecaca', fontWeight: 600, fontSize: 11, marginBottom: 2 }}>
                        Vente confirmée
                      </div>
                      <div style={{ color: 'var(--text)', fontWeight: 500 }}>
                        {Number.isFinite(prix) ? `${prix.toLocaleString('fr-CH')} CHF` : '—'}
                        {Number.isFinite(kmV) ? ` · ${kmV.toLocaleString('fr-CH')} km` : ''}
                        {y ? ` · ${y}` : ''}
                      </div>
                      {v.date_vente ? (
                        <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 2 }}>
                          Vendue le {new Date(v.date_vente).toLocaleDateString('fr-CH')}
                          {v.jours_en_ligne != null ? ` · ${v.jours_en_ligne} j en ligne` : ''}
                        </div>
                      ) : null}
                      {v.url ? (
                        <a
                          href={v.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#fca5a5', fontSize: 11, fontWeight: 600, marginTop: 4, display: 'inline-block' }}
                        >
                          Voir l&apos;annonce
                        </a>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}

          {showScatter ? (
            <div style={insightCard}>
              <div style={sectionTitle}>Prix vs kilométrage (annonces & ventes comparées)</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10, lineHeight: 1.45 }}>
                Blanc : même année · Gris : autre année · Rouge ouvert : vente confirmée · Rouge plein : votre véhicule · Halo rouge : baisse 14 j · Ligne bleu nuit : référence cotation.
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <ScatterChart margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="rgba(226,232,240,0.08)" />
                  <XAxis
                    type="number"
                    dataKey="km"
                    domain={[domains.kmMin, domains.kmMax]}
                    tick={{ fill: 'rgba(226,232,240,0.38)', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                    name="km"
                  />
                  <YAxis
                    type="number"
                    dataKey="prix"
                    domain={[domains.prixMin, domains.prixMax]}
                    tick={{ fill: 'rgba(226,232,240,0.38)', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
                    name="prix"
                  />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ScatterTooltip />} />
                  {Number.isFinite(valeurEstimee) && valeurEstimee > 0 ? (
                    <ReferenceLine
                      y={valeurEstimee}
                      stroke="rgba(0,30,80,0.55)"
                      strokeDasharray="4 4"
                    />
                  ) : null}
                  {Number.isFinite(params.km) ? (
                    <ReferenceLine
                      x={params.km}
                      stroke="rgba(226,232,240,0.22)"
                      strokeDasharray="4 4"
                    />
                  ) : null}
                  <Scatter
                    name="Autre année"
                    data={scatterData.autreAnnee}
                    fill="rgba(226,232,240,0.14)"
                    stroke="rgba(226,232,240,0.35)"
                    strokeWidth={1}
                    shape={(props) => <ScatterPointShape {...props} r={4.5} />}
                  />
                  <Scatter
                    name="Même année"
                    data={scatterData.memeAnnee}
                    fill="rgba(248,250,252,0.85)"
                    stroke="rgba(248,250,252,0.9)"
                    strokeWidth={1.1}
                    shape={(props) => <ScatterPointShape {...props} r={5.5} />}
                  />
                  <Scatter
                    name="Vendues"
                    data={scatterData.vendues}
                    fill="rgba(248,113,113,0.55)"
                    stroke="#f87171"
                    strokeWidth={1.25}
                    shape={(props) => <ScatterPointShape {...props} r={5.5} />}
                  />
                  {highlightPt.length ? (
                    <Scatter
                      name="Votre véhicule"
                      data={scatterData.votreAnnonce}
                      fill="#001E50"
                      stroke="#7ea6ff"
                      strokeWidth={2}
                      shape={(props) => <ScatterPointShape {...props} r={6} />}
                    />
                  ) : null}
                </ScatterChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: 14, marginTop: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                {[
                  { color: 'rgba(252,252,255,0.94)', label: `Même année (${scatterData.memeAnnee.length})` },
                  { color: 'rgba(226,232,240,0.2)', label: `Autre année (${scatterData.autreAnnee.length})` },
                  { color: 'rgba(248,113,113,0.55)', border: '#f87171', label: `Vendues (${scatterData.vendues.length})` },
                  { color: '#001E50', border: '#7ea6ff', label: 'Votre véhicule' },
                  { color: 'transparent', border: 'rgba(248,113,113,0.72)', label: 'Baisse 14 j (halo)' },
                  { color: 'rgba(226,232,240,0.18)', border: 'rgba(226,232,240,0.4)', label: 'Référence cotation' },
                ].map((l) => (
                  <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-tertiary)' }}>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: l.color,
                        border: l.border ? `2px solid ${l.border}` : 'none',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                      }}
                    />
                    {l.label}
                  </div>
                ))}
              </div>
            </div>
          ) : !loading && cotation ? (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>Nuage prix/km : pas assez de comparables.</div>
          ) : null}

          {!omitSimilairesExcerpt && similaires.length > 0 ? (
            <div style={insightCard}>
              <div style={sectionTitle}>Annonces proches (extrait)</div>
              <div
                style={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid rgba(148,163,184,0.14)',
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                {similaires.map((s, i) => (
                  <div
                    key={`${s.url || i}-${i}`}
                    style={{
                      padding: '8px 10px',
                      borderBottom: i < similaires.length - 1 ? '1px solid var(--border)' : 'none',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <div style={{ color: 'var(--text)', fontWeight: 500 }}>
                      {s.prix != null ? `${Number(s.prix).toLocaleString('fr-CH')} CHF` : '—'}
                      {s.km != null ? ` · ${Number(s.km).toLocaleString('fr-CH')} km` : ''}
                      {s.annee ? ` · ${s.annee}` : ''}
                    </div>
                    {[s.ville, s.canton].filter(Boolean).join(' · ') || null}
                    {s.url ? (
                      <div style={{ marginTop: 4 }}>
                        <a href={s.url} target="_blank" rel="noreferrer" style={{ color: '#bfdbfe', fontSize: 11, fontWeight: 600 }}>
                          Voir
                        </a>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {!omitHistoriqueChart && histSeries.length >= 2 ? (
        <div style={insightCard}>
          <div style={sectionTitle}>Historique des prix (chronologie)</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={histSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="rgba(226,232,240,0.08)" />
              <XAxis
                dataKey="idx"
                tick={{ fill: 'rgba(226,232,240,0.35)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Mouvements', position: 'insideBottom', offset: -2, fill: 'var(--text-tertiary)', fontSize: 10 }}
              />
              <YAxis
                dataKey="prix"
                tick={{ fill: 'rgba(226,232,240,0.35)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={44}
                tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
              />
              <Tooltip
                formatter={(v) => [`${Number(v).toLocaleString('fr-CH')} CHF`, 'Prix']}
                labelFormatter={(idx) => {
                  const row = histSeries.find((h) => String(h.idx) === String(idx))
                  return row?.t ? new Date(row.t).toLocaleString('fr-CH') : `Étape ${idx}`
                }}
                contentStyle={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="prix"
                stroke="#f87171"
                strokeWidth={2}
                dot={(dotProps) => {
                  const { cx, cy, payload } = dotProps
                  if (cx == null || cy == null) return null
                  const hot = Boolean(payload?.baisse_recente_ce_point)
                  return (
                    <g>
                      {hot ? (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={6}
                          fill="none"
                          stroke="rgba(248,113,113,0.75)"
                          strokeWidth={2}
                        />
                      ) : null}
                      <circle cx={cx} cy={cy} r={3} fill="#f87171" stroke="rgba(15,23,42,0.35)" strokeWidth={0.6} />
                    </g>
                  )
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : !omitHistoriqueChart && histSeries.length === 1 ? (
        <div style={insightCard}>
          <div style={sectionTitle}>Historique des prix</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Un seul palier enregistré : {histSeries[0].prix.toLocaleString('fr-CH')} CHF
          </div>
        </div>
      ) : null}
    </div>
  )
}
